import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { COLLECTIONS } from '../firebase/collections';
import DownloadIcon from '@mui/icons-material/Download';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const TechnicalServiceAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState('month');
  const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)));
  const [endDate, setEndDate] = useState(new Date());
  const [analyticsData, setAnalyticsData] = useState({
    totalRequests: 0,
    completedRequests: 0,
    averageResolutionTime: 0,
    requestsByStatus: {},
    requestsByPriority: {},
    requestsByType: {},
    requestsByLocation: {},
    timelineData: [],
    resolutionTimeByPriority: {},
    topIssues: [],
    satisfactionRating: 0
  });
  const { currentUser, userService } = useAuth();

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange, startDate, endDate, currentUser, userService]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');

      // Si userService n'est pas défini, on ne peut pas continuer
      if (!userService?.id) {
        setError('Service technique non trouvé. Veuillez contacter votre administrateur.');
        setLoading(false);
        return;
      }

      // Calculer les dates de début et de fin en fonction de la plage de temps sélectionnée
      let queryStartDate = startDate;
      let queryEndDate = endDate;

      if (timeRange === 'week') {
        queryStartDate = new Date();
        queryStartDate.setDate(queryStartDate.getDate() - 7);
      } else if (timeRange === 'month') {
        queryStartDate = new Date();
        queryStartDate.setMonth(queryStartDate.getMonth() - 1);
      } else if (timeRange === 'quarter') {
        queryStartDate = new Date();
        queryStartDate.setMonth(queryStartDate.getMonth() - 3);
      } else if (timeRange === 'year') {
        queryStartDate = new Date();
        queryStartDate.setFullYear(queryStartDate.getFullYear() - 1);
      }

      // Récupérer les demandes assignées à ce service technique
      // Note: On récupère toutes les demandes et on filtre côté client pour éviter les index composites
      const requestsQuery = query(
        collection(db, COLLECTIONS.WORK_REQUESTS),
        where('assignedServiceId', '==', userService.id)
      );

      const requestsSnapshot = await getDocs(requestsQuery);
      const allRequestsData = requestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        completedAt: doc.data().completedAt?.toDate() || null
      }));

      // Filtrer les demandes par date côté client
      const requestsData = allRequestsData
        .filter(req => req.createdAt >= queryStartDate && req.createdAt <= queryEndDate)
        .sort((a, b) => a.createdAt - b.createdAt);

      // Calculer les statistiques
      const totalRequests = requestsData.length;
      const completedRequests = requestsData.filter(req => req.status === 'completed').length;
      
      // Calculer le temps moyen de résolution (en heures)
      const completedRequestsWithTimes = requestsData.filter(
        req => req.status === 'completed' && req.completedAt && req.createdAt
      );
      
      let averageResolutionTime = 0;
      if (completedRequestsWithTimes.length > 0) {
        const totalResolutionTime = completedRequestsWithTimes.reduce((sum, req) => {
          const resolutionTime = (req.completedAt - req.createdAt) / (1000 * 60 * 60); // en heures
          return sum + resolutionTime;
        }, 0);
        averageResolutionTime = Math.round(totalResolutionTime / completedRequestsWithTimes.length);
      }

      // Regrouper les demandes par statut
      const requestsByStatus = requestsData.reduce((acc, req) => {
        const status = req.status || 'Non défini';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      // Regrouper les demandes par priorité
      const requestsByPriority = requestsData.reduce((acc, req) => {
        const priority = req.priority || 'Non défini';
        acc[priority] = (acc[priority] || 0) + 1;
        return acc;
      }, {});

      // Regrouper les demandes par type de problème
      const requestsByType = requestsData.reduce((acc, req) => {
        const type = req.problem || 'Non défini';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});

      // Regrouper les demandes par localisation
      const requestsByLocation = requestsData.reduce((acc, req) => {
        if (req.location && req.location.building) {
          const location = req.location.building;
          acc[location] = (acc[location] || 0) + 1;
        } else {
          acc['Non défini'] = (acc['Non défini'] || 0) + 1;
        }
        return acc;
      }, {});

      // Créer des données chronologiques pour les graphiques
      const timelineData = [];
      const dateMap = new Map();

      // Initialiser la carte avec toutes les dates dans la plage
      const currentDate = new Date(queryStartDate);
      while (currentDate <= queryEndDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        dateMap.set(dateStr, { date: new Date(currentDate), count: 0 });
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Compter les demandes par date
      requestsData.forEach(req => {
        const dateStr = req.createdAt.toISOString().split('T')[0];
        if (dateMap.has(dateStr)) {
          const entry = dateMap.get(dateStr);
          entry.count += 1;
          dateMap.set(dateStr, entry);
        }
      });

      // Convertir la carte en tableau pour le graphique
      dateMap.forEach((value) => {
        timelineData.push({
          date: value.date,
          count: value.count
        });
      });

      // Trier par date
      timelineData.sort((a, b) => a.date - b.date);

      // Calculer le temps de résolution moyen par priorité
      const resolutionTimeByPriority = {};
      const priorityGroups = {};

      completedRequestsWithTimes.forEach(req => {
        const priority = req.priority || 'Non défini';
        if (!priorityGroups[priority]) {
          priorityGroups[priority] = [];
        }
        
        const resolutionTime = (req.completedAt - req.createdAt) / (1000 * 60 * 60); // en heures
        priorityGroups[priority].push(resolutionTime);
      });

      for (const [priority, times] of Object.entries(priorityGroups)) {
        if (times.length > 0) {
          const average = times.reduce((sum, time) => sum + time, 0) / times.length;
          resolutionTimeByPriority[priority] = Math.round(average);
        }
      }

      // Identifier les problèmes les plus fréquents
      const problemCounts = {};
      requestsData.forEach(req => {
        const problem = req.problem || 'Non défini';
        problemCounts[problem] = (problemCounts[problem] || 0) + 1;
      });

      const topIssues = Object.entries(problemCounts)
        .map(([problem, count]) => ({ problem, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Calculer la note de satisfaction moyenne (simulée pour l'exemple)
      // Dans une vraie application, cette donnée viendrait d'une collection de feedback
      const satisfactionRating = 4.2;

      setAnalyticsData({
        totalRequests,
        completedRequests,
        averageResolutionTime,
        requestsByStatus,
        requestsByPriority,
        requestsByType,
        requestsByLocation,
        timelineData,
        resolutionTimeByPriority,
        topIssues,
        satisfactionRating
      });

    } catch (err) {
      console.error('Erreur lors de la récupération des données d\'analyse:', err);
      setError('Erreur lors du chargement des données d\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const exportToExcel = () => {
    try {
      // Créer un tableau de données pour l'export
      const exportData = [
        ['Rapport d\'analyse des services techniques'],
        ['Période:', `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`],
        ['Service:', userService?.name || 'Non spécifié'],
        [''],
        ['Indicateurs clés'],
        ['Total des demandes', analyticsData.totalRequests],
        ['Demandes complétées', analyticsData.completedRequests],
        ['Taux de résolution', `${Math.round((analyticsData.completedRequests / analyticsData.totalRequests) * 100)}%`],
        ['Temps moyen de résolution', `${analyticsData.averageResolutionTime} heures`],
        ['Note de satisfaction', analyticsData.satisfactionRating],
        [''],
        ['Demandes par statut'],
        ...Object.entries(analyticsData.requestsByStatus).map(([status, count]) => [status, count]),
        [''],
        ['Demandes par priorité'],
        ...Object.entries(analyticsData.requestsByPriority).map(([priority, count]) => [priority, count]),
        [''],
        ['Temps de résolution par priorité (heures)'],
        ...Object.entries(analyticsData.resolutionTimeByPriority).map(([priority, time]) => [priority, time]),
        [''],
        ['Top 5 des problèmes'],
        ...analyticsData.topIssues.map(issue => [issue.problem, issue.count])
      ];

      // Créer un classeur Excel
      const ws = XLSX.utils.aoa_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Rapport');

      // Générer le fichier Excel
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Télécharger le fichier
      saveAs(data, `Rapport_Service_Technique_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Erreur lors de l\'export Excel:', err);
      setError('Erreur lors de l\'export des données');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Analyse des performances
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={exportToExcel}
        >
          Exporter
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Période</InputLabel>
            <Select
              value={timeRange}
              onChange={handleTimeRangeChange}
              label="Période"
            >
              <MenuItem value="week">7 derniers jours</MenuItem>
              <MenuItem value="month">30 derniers jours</MenuItem>
              <MenuItem value="quarter">3 derniers mois</MenuItem>
              <MenuItem value="year">12 derniers mois</MenuItem>
              <MenuItem value="custom">Personnalisée</MenuItem>
            </Select>
          </FormControl>

          {timeRange === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Date de début"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                />
                <DatePicker
                  label="Date de fin"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                />
              </Box>
            </LocalizationProvider>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total des demandes
                </Typography>
                <Typography variant="h4">
                  {analyticsData.totalRequests}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Demandes complétées
                </Typography>
                <Typography variant="h4">
                  {analyticsData.completedRequests}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {analyticsData.totalRequests > 0 
                    ? `${Math.round((analyticsData.completedRequests / analyticsData.totalRequests) * 100)}%`
                    : '0%'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Temps moyen de résolution
                </Typography>
                <Typography variant="h4">
                  {analyticsData.averageResolutionTime}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Note de satisfaction
                </Typography>
                <Typography variant="h4">
                  {analyticsData.satisfactionRating}/5
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          sx={{ mb: 3 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Tendances" />
          <Tab label="Statuts" />
          <Tab label="Priorités" />
          <Tab label="Localisations" />
          <Tab label="Problèmes fréquents" />
        </Tabs>

        {/* Onglet Tendances */}
        {tabValue === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Évolution des demandes
            </Typography>
            {analyticsData.timelineData.length > 0 ? (
              <Box sx={{ height: 400 }}>
                <LineChart
                  xAxis={[{
                    data: analyticsData.timelineData.map(item => item.date),
                    scaleType: 'time',
                    valueFormatter: (date) => date.toLocaleDateString()
                  }]}
                  series={[
                    {
                      data: analyticsData.timelineData.map(item => item.count),
                      label: 'Nombre de demandes',
                      area: true,
                    },
                  ]}
                  height={400}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Aucune donnée disponible pour cette période
              </Typography>
            )}
          </Box>
        )}

        {/* Onglet Statuts */}
        {tabValue === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Répartition par statut
              </Typography>
              {Object.keys(analyticsData.requestsByStatus).length > 0 ? (
                <Box sx={{ height: 300 }}>
                  <PieChart
                    series={[
                      {
                        data: Object.entries(analyticsData.requestsByStatus).map(([name, value]) => ({
                          id: name,
                          value,
                          label: name
                        })),
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                      },
                    ]}
                    height={300}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucune donnée disponible
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Détails par statut
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Statut</TableCell>
                      <TableCell align="right">Nombre</TableCell>
                      <TableCell align="right">Pourcentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(analyticsData.requestsByStatus).map(([status, count]) => (
                      <TableRow key={status}>
                        <TableCell>{status}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                        <TableCell align="right">
                          {analyticsData.totalRequests > 0 
                            ? `${Math.round((count / analyticsData.totalRequests) * 100)}%`
                            : '0%'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}

        {/* Onglet Priorités */}
        {tabValue === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Répartition par priorité
              </Typography>
              {Object.keys(analyticsData.requestsByPriority).length > 0 ? (
                <Box sx={{ height: 300 }}>
                  <BarChart
                    dataset={Object.entries(analyticsData.requestsByPriority).map(([name, value]) => ({
                      priority: name,
                      count: value
                    }))}
                    yAxis={[{ scaleType: 'linear' }]}
                    xAxis={[{ scaleType: 'band', dataKey: 'priority' }]}
                    series={[{ dataKey: 'count', label: 'Nombre de demandes' }]}
                    height={300}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucune donnée disponible
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Temps de résolution par priorité
              </Typography>
              {Object.keys(analyticsData.resolutionTimeByPriority).length > 0 ? (
                <Box sx={{ height: 300 }}>
                  <BarChart
                    dataset={Object.entries(analyticsData.resolutionTimeByPriority).map(([name, value]) => ({
                      priority: name,
                      hours: value
                    }))}
                    yAxis={[{ scaleType: 'linear' }]}
                    xAxis={[{ scaleType: 'band', dataKey: 'priority' }]}
                    series={[{ dataKey: 'hours', label: 'Heures' }]}
                    height={300}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucune donnée disponible
                </Typography>
              )}
            </Grid>
          </Grid>
        )}

        {/* Onglet Localisations */}
        {tabValue === 3 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Répartition par bâtiment
              </Typography>
              {Object.keys(analyticsData.requestsByLocation).length > 0 ? (
                <Box sx={{ height: 300 }}>
                  <PieChart
                    series={[
                      {
                        data: Object.entries(analyticsData.requestsByLocation).map(([name, value]) => ({
                          id: name,
                          value,
                          label: name
                        })),
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                      },
                    ]}
                    height={300}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucune donnée disponible
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Détails par bâtiment
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bâtiment</TableCell>
                      <TableCell align="right">Nombre</TableCell>
                      <TableCell align="right">Pourcentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(analyticsData.requestsByLocation).map(([location, count]) => (
                      <TableRow key={location}>
                        <TableCell>{location}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                        <TableCell align="right">
                          {analyticsData.totalRequests > 0 
                            ? `${Math.round((count / analyticsData.totalRequests) * 100)}%`
                            : '0%'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}

        {/* Onglet Problèmes fréquents */}
        {tabValue === 4 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Top 5 des problèmes
              </Typography>
              {analyticsData.topIssues.length > 0 ? (
                <Box sx={{ height: 300 }}>
                  <BarChart
                    dataset={analyticsData.topIssues.map(issue => ({
                      problem: issue.problem,
                      count: issue.count
                    }))}
                    yAxis={[{ scaleType: 'linear' }]}
                    xAxis={[{ scaleType: 'band', dataKey: 'problem' }]}
                    series={[{ dataKey: 'count', label: 'Nombre de demandes' }]}
                    height={300}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucune donnée disponible
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Détails des problèmes fréquents
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Problème</TableCell>
                      <TableCell align="right">Nombre</TableCell>
                      <TableCell align="right">Pourcentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.topIssues.map((issue) => (
                      <TableRow key={issue.problem}>
                        <TableCell>{issue.problem}</TableCell>
                        <TableCell align="right">{issue.count}</TableCell>
                        <TableCell align="right">
                          {analyticsData.totalRequests > 0 
                            ? `${Math.round((issue.count / analyticsData.totalRequests) * 100)}%`
                            : '0%'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default TechnicalServiceAnalytics;
